import React, { useEffect, useState } from "react";
import api, { fmt, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { Plus, Trash, Lightning, X, ArrowCounterClockwise, Clock } from "@phosphor-icons/react";

const CCYS = ["USD", "EUR", "GBP", "AED", "INR", "JPY", "CHF", "SGD"];

export default function LoansPage() {
  const [rows, setRows] = useState([]);
  const [accts, setAccts] = useState([]);
  const [open, setOpen] = useState(false);
  const [repayFor, setRepayFor] = useState(null);
  const [repay, setRepay] = useState({ amount: "", interest_amount: "0", value_date: new Date().toISOString().slice(0, 10) });
  const [form, setForm] = useState({
    counterparty: "", principal: "", currency: "USD", interest_rate: "",
    start_date: new Date().toISOString().slice(0, 10), maturity_date: "",
    direction: "borrowed", bank_account_id: "", notes: "",
  });

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/loans"), api.get("/bank-accounts")]);
    setRows(a.data); setAccts(b.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/loans", {
      ...form, principal: parseFloat(form.principal), interest_rate: parseFloat(form.interest_rate),
      bank_account_id: form.bank_account_id || null,
    });
    setOpen(false);
    setForm({ ...form, counterparty: "", principal: "", interest_rate: "", maturity_date: "", notes: "" });
    load();
  };
  const accrue = async (id) => { await api.post(`/loans/${id}/accrue`); load(); };
  const remove = async (id) => { await api.delete(`/loans/${id}`); load(); };
  const runMaturity = async () => {
    const { data } = await api.post("/maturity/run");
    alert(`${data.posted} maturity transactions posted.`);
    load();
  };
  const submitRepay = async (e) => {
    e.preventDefault();
    await api.post(`/loans/${repayFor.id}/repay`, {
      amount: parseFloat(repay.amount || "0"),
      interest_amount: parseFloat(repay.interest_amount || "0"),
      value_date: repay.value_date,
    });
    setRepayFor(null);
    setRepay({ amount: "", interest_amount: "0", value_date: new Date().toISOString().slice(0, 10) });
    load();
  };

  return (
    <div>
      <PageHeader
        eyebrow="Instruments"
        title="Loans"
        subtitle="Borrowed and lent positions with accrual tracking."
        testid="loans-title"
        actions={
          <>
            <button onClick={runMaturity} data-testid="run-maturity-loan" className="border border-gray-300 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-gray-50 inline-flex items-center gap-2">
              <Clock size={14} weight="bold" /> Run Maturity
            </button>
            <button onClick={() => setOpen(true)} data-testid="new-loan-btn" className="bg-[#0020C2] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] flex items-center gap-2">
              <Plus size={14} weight="bold" /> New Loan
            </button>
          </>
        }
      />

      <div className="p-8">
        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="loans-table">
            <thead className="bg-gray-50">
              <tr className="text-left">
                {["ID", "Counterparty", "Dir", "Principal", "Ccy", "Rate %", "Start", "Maturity", "Outstanding", "Accrued", "Status", ""].map((h) => (
                  <th key={h} className={`px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300 ${["Principal","Rate %","Outstanding","Accrued"].includes(h) ? "text-right" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs">{r.id.slice(0, 8)}</td>
                  <td className="px-4 py-3 font-semibold">{r.counterparty}</td>
                  <td className="px-4 py-3 text-[10px] tracking-[0.15em] uppercase font-bold">{r.direction}</td>
                  <td className="px-4 py-3 text-right font-mono">{fmt(r.principal)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.currency}</td>
                  <td className="px-4 py-3 text-right font-mono">{r.interest_rate.toFixed(2)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{fmtDate(r.start_date)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{fmtDate(r.maturity_date)}</td>
                  <td className="px-4 py-3 text-right font-mono">{fmt(r.outstanding)}</td>
                  <td className="px-4 py-3 text-right font-mono">{fmt(r.accrued_interest)}</td>
                  <td className="px-4 py-3 text-[10px] tracking-[0.15em] uppercase font-bold">{r.status}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => accrue(r.id)} data-testid={`accrue-loan-${r.id}`} className="text-[#0020C2] hover:underline text-xs font-bold inline-flex items-center gap-1 mr-3">
                      <Lightning size={12} weight="bold" /> Accrue
                    </button>
                    <button onClick={() => setRepayFor(r)} data-testid={`repay-loan-${r.id}`} className="text-gray-900 hover:underline text-xs font-bold inline-flex items-center gap-1 mr-3">
                      <ArrowCounterClockwise size={12} weight="bold" /> Repay
                    </button>
                    <button onClick={() => remove(r.id)} data-testid={`del-loan-${r.id}`} className="text-[#DC2626] hover:underline text-xs font-bold">
                      <Trash size={12} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={12} className="px-4 py-12 text-center text-sm text-gray-500">No loans recorded yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="w-full max-w-md bg-white h-full overflow-y-auto" data-testid="loan-form">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-2xl font-black tracking-tight">New Loan</h2>
              <button type="button" onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Direction</label>
                <select value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })} data-testid="loan-direction" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                  <option value="borrowed">Borrowed (we owe)</option>
                  <option value="lent">Lent (owed to us)</option>
                </select>
              </div>
              {[
                ["counterparty", "Counterparty", "text"],
                ["principal", "Principal", "number"],
                ["interest_rate", "Interest Rate (% annual)", "number"],
                ["start_date", "Start Date", "date"],
                ["maturity_date", "Maturity Date", "date"],
              ].map(([k, label, type]) => (
                <div key={k}>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">{label}</label>
                  <input type={type} step="any" required value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`loan-${k}`} className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Currency</label>
                  <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="loan-currency" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                    {CCYS.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Bank Account</label>
                  <select value={form.bank_account_id} onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })} data-testid="loan-bank" className="w-full border border-gray-300 px-3 py-2.5 text-sm">
                    <option value="">— None —</option>
                    {accts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200">
              <button type="submit" data-testid="loan-submit" className="w-full bg-[#0020C2] text-white py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#001AA3]">Book loan</button>
            </div>
          </form>
        </div>
      )}

      {repayFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end" onClick={() => setRepayFor(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submitRepay} className="w-full max-w-md bg-white h-full overflow-y-auto" data-testid="repay-form">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Repayment</div>
                <h2 className="text-2xl font-black tracking-tight">{repayFor.counterparty}</h2>
                <div className="text-xs text-gray-500 font-mono mt-1">Outstanding: {fmt(repayFor.outstanding)} {repayFor.currency}</div>
              </div>
              <button type="button" onClick={() => setRepayFor(null)}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Principal Repayment</label>
                <input type="number" step="any" required value={repay.amount} onChange={(e) => setRepay({ ...repay, amount: e.target.value })} data-testid="repay-amount" className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Interest Payment (optional)</label>
                <input type="number" step="any" value={repay.interest_amount} onChange={(e) => setRepay({ ...repay, interest_amount: e.target.value })} data-testid="repay-interest" className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0020C2]" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block mb-1.5">Value Date</label>
                <input type="date" required value={repay.value_date} onChange={(e) => setRepay({ ...repay, value_date: e.target.value })} data-testid="repay-date" className="w-full border border-gray-300 px-3 py-2.5 text-sm" />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200">
              <button type="submit" data-testid="repay-submit" className="w-full bg-[#0020C2] text-white py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#001AA3]">Post repayment</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
